import { MessageDescriptor } from 'react-intl';

import { removeAccent } from '@savgroup-front-common/core/formatters/removeAccent';
import { BaseAutocompleteOptionForHookForm } from '@savgroup-front-common/types';
import {
  COMPANY_DIRECTORY_SOURCE,
  CompanyDirectorySource,
} from '@savgroup-front-common/types/CompanyDirectory';

import messages from './messages';
import {
  CNAME_FALLBACK_PREFIX,
  CNAME_MAX_LENGTH,
  CNAME_MIN_LENGTH,
  CNAME_PATTERN,
} from './OnboardingForm.constants';

const NON_ALPHANUMERIC_PATTERN = /[^a-z0-9]+/g;
const EDGE_DASHES_PATTERN = /^-+|-+$/g;

export function slugifyCompanyName(companyName?: string): string | undefined {
  const slug = removeAccent(companyName ?? '')
    .toLowerCase()
    .replace(NON_ALPHANUMERIC_PATTERN, '-')
    .replace(EDGE_DASHES_PATTERN, '')
    .slice(0, CNAME_MAX_LENGTH)
    .replace(EDGE_DASHES_PATTERN, '');

  if (!slug) {
    return undefined;
  }

  return slug.length >= CNAME_MIN_LENGTH
    ? slug
    : `${CNAME_FALLBACK_PREFIX}-${slug}`;
}

export const isValidCName = (value?: string): boolean =>
  !!value &&
  value.length >= CNAME_MIN_LENGTH &&
  value.length <= CNAME_MAX_LENGTH &&
  CNAME_PATTERN.test(value);

export function getDomainSuffix({
  cName,
  domainName,
}: {
  cName: string;
  domainName: string;
}): string {
  return domainName.startsWith(cName)
    ? domainName.slice(cName.length)
    : domainName;
}

export function buildCNameCandidate({
  slug,
  attempt,
}: {
  slug: string;
  attempt: number;
}): string {
  if (attempt === 0) {
    return slug;
  }

  const suffix = `-${attempt + 1}`;

  return `${slug.slice(0, CNAME_MAX_LENGTH - suffix.length).replace(/-+$/, '')}${suffix}`;
}

export const findCountryOption = ({
  countryOptions,
  countryCode,
}: {
  countryOptions: BaseAutocompleteOptionForHookForm[];
  countryCode?: string;
}): BaseAutocompleteOptionForHookForm | undefined =>
  countryCode
    ? countryOptions.find(
        (option) => option.value.toUpperCase() === countryCode.toUpperCase(),
      )
    : undefined;

export function getSourceMessage(
  source: CompanyDirectorySource,
): MessageDescriptor | undefined {
  switch (source) {
    case COMPANY_DIRECTORY_SOURCE.FRENCH_COMPANY_REGISTRY:
      return messages.sourceFrenchCompanyRegistry;
    case COMPANY_DIRECTORY_SOURCE.COMPANIES_HOUSE:
      return messages.sourceCompaniesHouse;
    case COMPANY_DIRECTORY_SOURCE.BELGIAN_CROSSROADS_BANK:
      return messages.sourceBelgianCrossroadsBank;
    case COMPANY_DIRECTORY_SOURCE.GLEIF:
      return messages.sourceGleif;
    default:
      source satisfies never;

      return undefined;
  }
}
